"use client";

import React, { useState, useEffect, useRef } from 'react';
import axios from "axios";
import toast from 'react-hot-toast';
import BaseMap from './BaseMap';
import UnitsOverlay from './UnitsOverlay';
import DirectionalIndicators from './DirectionalIndicators';
import './MyMap.scss';

let count = 0

const MyMap = (props) => {
  count++
  
  const selectedUnit = props.selectedUnit
  const setSelectedUnit = props.setSelectedUnit
  const [mapCenter, setMapCenter] = useState({ lat: 36.132235, lng: -5.352273 })
  const [mapZoom, setMapZoom] = useState(5)
  const [unitHistory, setUnitHistory] = useState({})
  const [mapState, setMapState] = useState({ center: null, zoom: 5, bounds: null })
  const [isDragging, setIsDragging] = useState(false)
  const [userHasPanned, setUserHasPanned] = useState(false)
  const [hasInitialCentered, setHasInitialCentered] = useState(false)
  const [showZoomLabel, setShowZoomLabel] = useState(false)
  const [currentZoom, setCurrentZoom] = useState(5)
  const zoomTimeoutRef = useRef(null)
  const isProgrammaticZoom = useRef(false)
  
  const mapRef = useRef(null)
  const mapContainerRef = useRef(null)
  const lastMapCenterUpdate = useRef(null)


  const units2 = Array.isArray(props.data) ? props.data : []
  const stateData = props.stateData

  // Center map on first unit when data loads (most recently updated)
  useEffect(() => {
    if (units2.length > 0 && !selectedUnit && !hasInitialCentered && mapRef.current) {
      // Find unit with most recent last_update
      const firstUnit = units2.reduce((latest, unit) => {
        if (!latest.last_update) return unit
        if (!unit.last_update) return latest
        return new Date(unit.last_update) > new Date(latest.last_update) ? unit : latest
      })
      
      if (firstUnit.last_pos_latitude && firstUnit.last_pos_longitude) {
        mapRef.current.panTo({ lat: firstUnit.last_pos_latitude, lng: firstUnit.last_pos_longitude })
        setHasInitialCentered(true)
      }
    }
  }, [units2, selectedUnit, hasInitialCentered, mapRef.current])

  // Predict where unit will be in 10 seconds
  const predictUnitPosition = (unitId) => {
    const history = unitHistory[unitId]
    if (!history || history.length < 2) return null
    
    const latest = history[history.length - 1]
    const previous = history[history.length - 2]
    const timeDiff = (latest.timestamp - previous.timestamp) / 1000
    
    if (timeDiff === 0) return latest
    
    const latVelocity = (latest.lat - previous.lat) / timeDiff
    const lngVelocity = (latest.lng - previous.lng) / timeDiff
    
    return {
      lat: latest.lat + (latVelocity * 10),
      lng: latest.lng + (lngVelocity * 10)
    }
  }

  // Check if unit is near map border
  const isUnitNearBorder = (unitLat, unitLng, map) => {
    if (!map) return false
    
    const bounds = map.getBounds()
    if (!bounds) return false
    
    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()
    
    const latRange = ne.lat() - sw.lat()
    const lngRange = ne.lng() - sw.lng()
    const threshold = 0.15 // 30% from edge
    
    const distFromNorth = ne.lat() - unitLat
    const distFromSouth = unitLat - sw.lat()
    const distFromEast = ne.lng() - unitLng
    const distFromWest = unitLng - sw.lng()
    
    return (
      distFromNorth < latRange * threshold ||
      distFromSouth < latRange * threshold ||
      distFromEast < lngRange * threshold ||
      distFromWest < lngRange * threshold
    )
  }

  // Track unit history and update map center for ongoing tracking only
  useEffect(() => {
    if (selectedUnit && Array.isArray(units2) && !userHasPanned) {
      const unit = units2.find(u => u.unit_id === selectedUnit)
      if (unit && unit.last_pos_latitude && unit.last_pos_longitude) {
        const now = Date.now()
        
        setUnitHistory(prev => {
          const history = prev[selectedUnit] || []
          const newHistory = [...history, {
            lat: unit.last_pos_latitude,
            lng: unit.last_pos_longitude,
            timestamp: now
          }].slice(-3)
          
          // Only use prediction if unit is near border and we have movement history
          if (isUnitNearBorder(unit.last_pos_latitude, unit.last_pos_longitude, mapRef.current) && newHistory.length >= 2) {
            const latest = newHistory[newHistory.length - 1]
            const previous = newHistory[newHistory.length - 2]
            const timeDiff = (latest.timestamp - previous.timestamp) / 1000
            
            if (timeDiff > 0) {
              const latVelocity = (latest.lat - previous.lat) / timeDiff
              const lngVelocity = (latest.lng - previous.lng) / timeDiff
              
              // Calculate predicted position with shorter prediction time
              const predictedLat = latest.lat + (latVelocity * 15)
              const predictedLng = latest.lng + (lngVelocity * 15)
              
              const centerPos = { lat: predictedLat, lng: predictedLng }
              const centerKey = `${centerPos.lat},${centerPos.lng}`
              
              if (lastMapCenterUpdate.current !== centerKey) {
                lastMapCenterUpdate.current = centerKey
                mapRef.current.setCenter(centerPos)
                setMapCenter(centerPos)
              }
            }
          }
          
          return { ...prev, [selectedUnit]: newHistory }
        })
      }
    }
  }, [selectedUnit, units2, mapZoom, userHasPanned])

  // Custom smooth animation function
  const animateToPosition = (targetLat, targetLng, targetZoom = 13, duration = 1000) => {
    if (!mapRef.current) return
    
    isProgrammaticZoom.current = true
    const startCenter = mapRef.current.getCenter()
    const startZoom = mapRef.current.getZoom()
    const startTime = Date.now()
    
    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      
      // Easing function for smooth animation
      const easeInOutCubic = t => t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1
      const easedProgress = easeInOutCubic(progress)
      
      // Interpolate position
      const currentLat = startCenter.lat() + (targetLat - startCenter.lat()) * easedProgress
      const currentLng = startCenter.lng() + (targetLng - startCenter.lng()) * easedProgress
      const currentZoom = startZoom + (targetZoom - startZoom) * easedProgress
      
      mapRef.current.setCenter({ lat: currentLat, lng: currentLng })
      mapRef.current.setZoom(currentZoom)
      
      if (progress < 1) {
        requestAnimationFrame(animate)
      } else {
        isProgrammaticZoom.current = false
      }
    }
    
    animate()
  }

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current)
      }
    }
  }, [])

  const handleUnitSelect = (unitId) => {
    const newSelection = unitId === selectedUnit ? null : unitId
    setSelectedUnit(newSelection)
    setUserHasPanned(false) // Reset panned state when selecting new unit
    
    // Immediately center on selected unit with animation
    if (newSelection && Array.isArray(units2) && mapRef.current) {
      const unit = units2.find(u => u.unit_id === newSelection)
      if (unit && unit.last_pos_latitude && unit.last_pos_longitude) {
        
        // Use custom smooth animation
        animateToPosition(unit.last_pos_latitude, unit.last_pos_longitude, 13, 1000)
        
        setMapCenter({ lat: unit.last_pos_latitude, lng: unit.last_pos_longitude })
        setMapZoom(13)
      }
    }
  }

  const handleMapReady = (map) => {
    mapRef.current = map
    
    // Listen for drag events to allow panning while unit is selected
    map.addListener('dragstart', () => setIsDragging(true))
    map.addListener('dragend', () => {
      setIsDragging(false)
      if (selectedUnit) {
        setUserHasPanned(true) // User has manually panned away from selected unit
      }
    })
    
    // Listen for zoom changes to break controlled mode
    map.addListener('zoom_changed', () => {
      if (selectedUnit && !isDragging && !isProgrammaticZoom.current) {
        const currentZoom = map.getZoom()
        if (currentZoom !== mapZoom) {
          setUserHasPanned(true) // User has manually zoomed, break controlled mode
        }
      }
    })
  }

  const handleMapChange = ({ center, zoom, bounds }) => {
    setMapState({ center, zoom, bounds })
    setMapZoom(zoom) // Update mapZoom state
    
    // Show zoom label when zoom changes
    if (zoom !== currentZoom) {
      setCurrentZoom(zoom)
      setShowZoomLabel(true)
      
      // Clear existing timeout
      if (zoomTimeoutRef.current) {
        clearTimeout(zoomTimeoutRef.current)
      }
      
      // Hide label after 2 seconds
      zoomTimeoutRef.current = setTimeout(() => {
        setShowZoomLabel(false)
      }, 2000)
    }
  }

  const handleMapClick = () => {
    if (selectedUnit) {
      setSelectedUnit(null)
    }
  }

  const handleRecenterUnit = () => {
    if (selectedUnit && Array.isArray(units2) && mapRef.current) {
      const unit = units2.find(u => u.unit_id === selectedUnit)
      if (unit && unit.last_pos_latitude && unit.last_pos_longitude) {

        // Get current zoom level from map
        const currentZoom = mapRef.current.getZoom()
        
        // Zoom in if too zoomed out, otherwise keep current zoom
        const targetZoom = currentZoom < 13 ? 13 : currentZoom
        
        // Use custom smooth animation
        animateToPosition(unit.last_pos_latitude, unit.last_pos_longitude, targetZoom, 1000)
        
        setMapCenter({ lat: unit.last_pos_latitude, lng: unit.last_pos_longitude })
        setMapZoom(targetZoom) // Update zoom state to match
        setUserHasPanned(false) // Return to controlled state
      }
    }
  }

  return (
    <div 
      ref={mapContainerRef}
      id="mapArea" 
      style={{ 
        height: "calc(100vh - 200px)", 
        maxHeight: "669px",
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      <BaseMap
        onMapReady={handleMapReady}
        onMapChange={handleMapChange}
        onClick={handleMapClick}
        selectedUnit={selectedUnit}
      />
      
      <UnitsOverlay
        units={units2}
        map={mapRef.current}
        mapContainer={mapContainerRef.current}
        selectedUnit={selectedUnit}
        onUnitSelect={handleUnitSelect}
        stateData={stateData}
        setActiveUnit={props.setActiveUnit}
        onContextMenu={props.openContextMenuHandler}
        onSessionVideos={props.onSessionVideos}
        onOpenUnit={props.onOpenUnit}
      />
      
      <DirectionalIndicators
        units={units2}
        map={mapRef.current}
        mapContainer={mapContainerRef.current}
        onUnitSelect={handleUnitSelect}
        selectedUnit={selectedUnit}
        onRecenterUnit={handleRecenterUnit}
      />
      
      {/* Loading overlay */}
      {units2.length === 0 && (
        <div className="map-loading-overlay">
          <div className="map-spinner"></div>
          <div className="map-loading-text">Loading units...</div>
        </div>
      )}
      
      {/* Zoom level label */}
      {showZoomLabel && (
        <div 
          className="btn"
          style={{
            position: 'absolute',
            bottom: '10px',
            left: '10px',
            zIndex: 1000,
            pointerEvents: 'none',
            color: '#fff',
            backgroundColor: 'rgba(45, 206, 137, 0.75)',
            transition: 'opacity 0.3s ease-out',
            opacity: showZoomLabel ? 1 : 0,
            fontSize: '12px'
          }}
        >
          Zoom: {Math.round(currentZoom)}
        </div>
      )}

      {/* Recenter button - show when unit is selected and user has panned */}
      {selectedUnit && userHasPanned && (
        <button
          onClick={handleRecenterUnit}
          className="btn map-button"
        >
          Re-center unit {selectedUnit}
        </button>
      )}
    </div>
  )
}

export default MyMap
